"use client";

import { Box, type BoxProps } from "@chakra-ui/react";
import { useEffect, useRef, useState } from "react";

interface RevealProps extends BoxProps {
  delay?: number;
  /** Starting transform before reveal, e.g. "translateY(40px) scale(0.85)". */
  from?: string;
  children: React.ReactNode;
}

/**
 * Fade-and-rise reveal for marketing sections, triggered once the element
 * scrolls into view. IntersectionObserver + CSS transition only, no
 * animation library. Content is present in the DOM immediately (SEO/no-JS
 * safe) — only the hidden starting state is applied after mount, and only
 * if the element isn't already on screen (avoids a pointless blink on
 * initial load or back-navigation).
 */
export const Reveal = ({
  delay = 0,
  from = "translateY(24px)",
  children,
  ...boxProps
}: RevealProps) => {
  const ref = useRef<HTMLDivElement>(null);
  // null = pre-hydration (render visible, no transition); false = armed and
  // hidden; true = revealed.
  const [shown, setShown] = useState<boolean | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (el == null) {
      return;
    }
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setShown(true);
      return;
    }
    const rect = el.getBoundingClientRect();
    if (rect.top < window.innerHeight && rect.bottom > 0) {
      setShown(true);
      return;
    }
    setShown(false);
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShown(true);
          observer.disconnect();
        }
      },
      { rootMargin: "0px 0px -60px 0px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <Box
      ref={ref}
      transitionProperty={shown != null ? "transform, opacity" : undefined}
      transitionDuration={shown != null ? "0.7s" : undefined}
      transitionTimingFunction={shown != null ? "ease-out" : undefined}
      transitionDelay={shown === false ? undefined : `${delay}ms`}
      transform={shown === false ? from : "translateY(0) scale(1)"}
      opacity={shown === false ? 0 : 1}
      {...boxProps}
    >
      {children}
    </Box>
  );
};
